// Marva web-app documentation — rewritten 2026-06-09 to match actual product
// functionality (see the codebase fact-find). Authored as structured content so
// the Docs screen can render the After Hours article layout (lede, callouts,
// check-lists, serif emphasis) with full fidelity.
//
// Inline markup inside any `text`:  **bold**  ·  _serif-italic emphasis_  ·  `code`


export type DocBlock =
  | { type: 'h2'; text: string }
  | { type: 'p'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'callout'; text: string }
  | { type: 'code'; text: string }

export interface DocArticle {
  id: string
  group: string
  title: string
  lede: string
  blocks: DocBlock[]
}

export const DOC_GROUPS: string[] = [
  'Getting started',
  'Working with Marva',
  'Channels',
  'Reference',
]

export const ARTICLES: DocArticle[] = [
  // ---------------------------------------------------------------- Getting started
  {
    id: 'welcome',
    group: 'Getting started',
    title: 'Welcome to Marva',
    lede: "Marva is an autonomous teammate for your Shopify store. She talks to your customers on chat, email and SMS — remembering each one — and handles the follow-ups you'd never have time for.",
    blocks: [
      { type: 'p', text: "Marva watches what's happening in your store — a cart left behind, a product browsed and dropped, an item back in stock — and decides, customer by customer, whether to reach out, on which channel, and what to say. Every message she sends can be replied to: when a customer answers, she keeps the conversation going rather than dropping them into a void." },
      { type: 'h2', text: 'What makes her different' },
      { type: 'list', items: [
        '**Memory-first.** Marva carries a profile of every customer across chat, email and SMS — what they have bought, how they shop, what they care about — so each message sounds like it remembers them.',
        '**She decides; you do not build flows.** There are no campaign builders or if-this-then-that trees. Marva reads the situation and chooses the moment, the channel and the words.',
        '**Earned autonomy.** She starts by watching and drafting — sending nothing — and only acts once you have seen her work and turned her on.',
      ] },
      { type: 'h2', text: 'The shape of it' },
      { type: 'p', text: 'Something happens → Marva decides what to do, and writes down why → she acts on the right channel → the outcome is recorded against that decision. The _Ledger_ shows you everything she did, _Knowledge_ shows you who she knows, and _Authority_ is where you set what she is allowed to do.' },
      { type: 'callout', text: "New here? Start in **Shadow mode** — Marva drafts everything but sends nothing, so you can read what she _would_ have done before trusting her with the real thing." },
    ],
  },
  {
    id: 'trust',
    group: 'Getting started',
    title: 'How trust works',
    lede: 'Marva earns autonomy the way a good new hire would — one correction at a time. You decide how far she goes, and you can change your mind any moment.',
    blocks: [
      { type: 'h2', text: 'The autonomy ladder' },
      { type: 'p', text: "You set Marva's level on the _Authority → Authority_ tab, and the trust pill in the top bar always shows where she stands. Changes take effect from her very next message." },
      { type: 'list', items: [
        '**Shadow** — she watches and drafts, and sends nothing she starts herself; her replies to customers who write in are held too. You read what she _would_ have done. Every store begins here.',
        '**Assisted** — she acts within the limits you set. Most stores live here.',
        '**Autonomous** _(coming soon)_ — she will handle conversations end to end and surface only the exceptional.',
      ] },
      { type: 'callout', text: '**The shadow log never stops.** Even once she is live, Marva records every decision the way she would have made it — so promoting or demoting her is never a leap of faith.' },
      { type: 'h2', text: 'What “a line” means' },
      { type: 'p', text: `Lines are the guardrails you draw on _Authority → Limits_ — your discount ceiling, protected products, quiet hours, and the things that always come to you. Marva stays inside them, whatever her level. What she does above your ceiling is shown beside it on _Limits_.` },
      { type: 'h2', text: 'Going live' },
      { type: 'p', text: 'Moving from Shadow to Assisted is the moment Marva starts sending. Going live needs an active subscription — if you are still on trial, she will point you to your plan first.' },
    ],
  },
  {
    id: 'connect',
    group: 'Getting started',
    title: 'Connecting your store',
    lede: 'Marva runs on your Shopify data. Connecting takes a couple of minutes and a one-time approval.',
    blocks: [
      { type: 'h2', text: 'What gets connected' },
      { type: 'list', items: [
        '**Your catalogue, customers and orders** sync in, so Marva knows what you sell and who buys it.',
        '**Real-time webhooks** keep her current — new orders, abandoned carts, inventory and price changes arrive the moment they happen.',
        '**A tracking script** feeds the on-site behavioural stream: what shoppers browse and where they hesitate. On Shopify it is installed for you; for custom storefronts you can copy it from _Account → Integrations_.',
      ] },
      { type: 'h2', text: 'Keeping it in sync' },
      { type: 'p', text: 'Marva stays current automatically through webhooks. If you ever want to force a full re-pull of customers, products and orders, use **Sync data** under _Account → Integrations → Shopify → Manage_.' },
      { type: 'callout', text: 'Your Shopify access token and other credentials are encrypted at rest. Marva only reads what she needs to do her job.' },
    ],
  },
  // ---------------------------------------------------------------- Working with Marva
  {
    id: 'journal',
    group: 'Working with Marva',
    title: 'Reading the journal',
    lede: 'The Journal is Marva’s diary of your shop — everything she did, and why, any day you want to look back.',
    blocks: [
      { type: 'h2', text: "What you'll see" },
      { type: 'p', text: 'Entries are grouped by day into morning, afternoon and evening. Each one tells you _what_ happened in a line — a cart recovered, a browser followed up, a question answered. Open an entry to read **why** she made that call, the context she remembered, and the message itself.' },
      { type: 'h2', text: 'Stepping back' },
      { type: 'p', text: 'The Journal opens on the latest day. Use the date navigator to step back a day at a time, or zoom out to 30, 90 or 365 days, where the top becomes a rollup: conversations handled, decisions made, discounts held, and revenue attributed.' },
      { type: 'callout', text: 'Revenue in the Journal is **Marva-attributed** revenue — sales she can connect to something she did — not your store’s total. Each channel has its own attribution window.' },
      { type: 'h2', text: 'Teaching her' },
      { type: 'p', text: 'Every decision asks _Was this the right call?_ — answer _Yes, do this again_, or _No — let me correct it_ and say what should change: the tone, the timing, the channel, what she said, the offer, or something else. You have a few seconds to undo before it is recorded. That feedback is part of how Marva learns your taste.' },
    ],
  },
  {
    id: 'memory',
    group: 'Working with Marva',
    title: 'How memory works',
    lede: 'Marva remembers your customers so every message sounds like it knows them. Knowledge is where you see what she has learned, and where she got it.',
    blocks: [
      { type: 'h2', text: 'What she remembers' },
      { type: 'list', items: [
        '**Where each customer is with you** — prospect, first purchase, repeat, loyal, VIP, at-risk or churned — plus a value segment and a churn-risk read.',
        '**How they shop** — their style and archetype, what drives or blocks a purchase, and what they are likely to need next.',
        '**What they have bought** — order patterns, reorder candidates, size and category preferences, return behaviour.',
        '**What was said** — a running summary of past conversations and any open threads, across chat, email and SMS.',
      ] },
      { type: 'h2', text: 'Where it comes from' },
      { type: 'p', text: 'Marva synthesises this quietly in the background — from conversations, orders and on-site behaviour — into a single profile per customer. Products get the same treatment: an _expert card_ of selling points, fit and real-world research she can draw on when a shopper asks.' },
      { type: 'h2', text: 'The Knowledge screen' },
      { type: 'p', text: 'Three tabs. **To check** opens on the beliefs she is least sure of — first the patterns, the same belief about several customers that you can settle together, then one customer (or, with **Who**, one product) at a time, each with its own % and the date she formed it. Search finds the customers and products loaded there. **What she learned** lists every belief she has formed, newest first, and ends with **Raw events** — the events, sessions, carts and orders arriving as they land, for checking the connection. **Your answers** holds the replies she reuses so a question never reaches you twice.' },
    ],
  },
  {
    id: 'guardrails',
    group: 'Working with Marva',
    title: 'Setting guardrails',
    lede: 'Guardrails are the lines Marva will not cross on her own. You set them once on Authority → Limits; she respects them on every message.',
    blocks: [
      { type: 'h2', text: 'Discounts' },
      { type: 'p', text: 'Set a maximum discount and Marva will never exceed it. You can tighten it further per channel and per customer type, protect specific products, categories or tags from ever being discounted, and cap how much she can give away over a day, week or month. Push past a threshold and she escalates to you instead of deciding alone.' },
      { type: 'h2', text: 'Frequency' },
      { type: 'p', text: 'Marva caps how often she contacts a customer so she never fatigues them — with a minimum gap between messages and a ceiling on SMS per customer each month. The exact limits adapt to your store’s type; you will find them on _Authority → Limits_.' },
      { type: 'h2', text: 'Voice and escalation' },
      { type: 'list', items: [
        '**Brand voice** — set the tone she writes in on _Authority → Brand → How we sound_, and she carries it into every channel.',
        '**Always-ask** — the sensitive calls, like anything touching refunds, come to you regardless of her autonomy level.',
      ] },
      { type: 'callout', text: "Guardrails apply at every level — even Assisted. They are the difference between an agent that is _autonomous_ and one that is _unsupervised_." },
    ],
  },
  {
    id: 'touchpoints',
    group: 'Working with Marva',
    title: 'Touchpoints',
    lede: 'Touchpoints are the moments Marva reaches out. Each one is reply-enabled — answer her and the conversation continues.',
    blocks: [
      { type: 'h2', text: 'The touchpoints' },
      { type: 'list', items: [
        '**Cart Recovery** _(always on)_ — she wins back abandoned carts over SMS and email, choosing the timing and channel herself.',
        '**Browse Follow-Up** — re-engages high-intent shoppers who left without buying.',
        '**Back-in-Stock Alerts** — tells the people who wanted it the moment it returns.',
        '**Price Drop Alerts** — nudges price-sensitive shoppers when a watched product falls.',
      ] },
      { type: 'p', text: 'Cart Recovery is always on. The rest are conversations she starts that nobody asked for, so they follow her authority: on _Authority_, the row **Starts a conversation you did not ask for** switches them all off, or back on, together.' },
      { type: 'callout', text: 'Every touchpoint obeys your **frequency caps**, so turning several on will not mean a customer hears from Marva more than you would want.' },
    ],
  },
  // ---------------------------------------------------------------- Channels
  {
    id: 'email',
    group: 'Channels',
    title: 'Email & domains',
    lede: 'Marva sends from your own domain, so her emails land as you — not as a third party.',
    blocks: [
      { type: 'h2', text: 'Sending as you' },
      { type: 'p', text: 'Connect a domain under _Account → Integrations → Email_ and Marva sends from an address on it (like hello@your-store) with your reply-to. You choose the from-name and address.' },
      { type: 'h2', text: 'Verification' },
      { type: 'list', items: [
        '**DKIM** — three CNAME records that let Marva sign mail as you.',
        '**SPF** — one TXT record authorising the send.',
        '**DMARC** — one optional TXT record for the strongest deliverability.',
      ] },
      { type: 'p', text: 'Add the records at your DNS provider and Marva verifies them automatically — usually within a few hours. The Integrations panel shows _Verified_ once SPF, DKIM and DMARC are passing.' },
      { type: 'callout', text: 'Marva protects your sender reputation: hard bounces and complaints are suppressed automatically, so you keep emailing the people who want to hear from you.' },
    ],
  },
  {
    id: 'sms',
    group: 'Channels',
    title: 'SMS (bring your own)',
    lede: 'SMS is bring-your-own — you connect your own Twilio account, so you keep your number and your carrier rates.',
    blocks: [
      { type: 'h2', text: 'Connecting Twilio' },
      { type: 'p', text: 'Add your Twilio Account SID, Auth Token and (recommended) Messaging Service SID under _Account → Integrations → SMS_. Your credentials are encrypted at rest. A Messaging Service SID keeps you aligned with A2P 10DLC carrier rules.' },
      { type: 'h2', text: 'Staying compliant' },
      { type: 'list', items: [
        // One rule for every text (MAR-159): `evaluateQuietHours` unions the merchant's SMS window
        // with the 9pm–8am floor, called by `checkCanSendSms` for every send and by the reply
        // preflight. ONE exemption (MAR-159 review, pending counsel): an immediate answer — within
        // 15 minutes of the shopper's own text — is exempt from window and floor, because it
        // answers rather than solicits. A reply that is not immediate is held and recorded
        // (`withheld_compliance`), never sent later and never dropped silently.
        '**Quiet hours** — Marva starts no text in the hours you set, and never texts between 9pm and 8am whatever you set, because that is the window US telemarketing rules protect. When a customer texts her, she answers straight away — within 15 minutes of their message — whatever the hour, because that is an answer, not a sales message; a reply that cannot go out straight away is not sent later, and shows on your Ledger as held. Quiet hours that would cover the whole day are refused — to stop texts, switch SMS off.',
        '**Per-customer caps** — a monthly ceiling on texts per customer, so SMS stays a signal, not noise.',
        '**Permanent opt-out** — a customer who opts out is suppressed for good.',
      ] },
      { type: 'p', text: 'Choose which moments use SMS — cart abandonment, back-in-stock, flash sales — in the SMS settings; everything else stays on email and chat.' },
    ],
  },
  // ---------------------------------------------------------------- Reference
  {
    id: 'mau',
    group: 'Reference',
    title: 'What counts as a MAU',
    lede: 'Your plan is sized by active customers — the people Marva is actively remembering and working for.',
    blocks: [
      { type: 'h2', text: "What's metered" },
      { type: 'p', text: 'Marva tracks four things against your plan each month: **active customers** (the customer profiles she is holding), **conversations**, **messages**, and **products**. Your MAU is that active-customers figure.' },
      { type: 'h2', text: 'Plans' },
      { type: 'p', text: 'Plans step up the limits, not the assistant — every plan gets the same Marva, just more headroom. You will find your current usage and limits, and can change plan, under _Account → Billing_.' },
      { type: 'callout', text: 'Approaching a limit? Marva warns you well before you hit it, with a short grace period — she will not stop mid-conversation without notice.' },
    ],
  },
  {
    id: 'privacy',
    group: 'Reference',
    title: 'Data & privacy',
    lede: 'Marva only holds what she needs to do the job, and protects it carefully.',
    blocks: [
      { type: 'h2', text: 'What she stores' },
      { type: 'p', text: 'Customer and order records from Shopify, the conversations she has, and the things she observes about how people shop. Sensitive details — emails, phone numbers, addresses, and your channel credentials — are encrypted at rest.' },
      { type: 'h2', text: 'Consent and contact capture' },
      { type: 'p', text: "When Marva captures a contact on your site, explicit consent is required and recorded with it. Marketing consent follows Shopify's own subscribed / unsubscribed state — she will not message someone who has not opted in." },
      { type: 'h2', text: 'Unsubscribes and opt-outs' },
      { type: 'list', items: [
        '**Email** — every email carries a working unsubscribe link (CAN-SPAM); unsubscribes are honoured immediately and permanently, and you can point them at your own landing page.',
        '**SMS** — opt-outs are permanent, and quiet hours are enforced.',
      ] },
      { type: 'callout', text: 'Short version: explicit consent in, easy opt-out out, encryption throughout — and Marva never messages people who have said no.' },
    ],
  },
]

export function articleById(id: string): DocArticle | undefined {
  return ARTICLES.find((a) => a.id === id)
}
